import { isConnected } from '../../../shared/utils/is';
import { showUploadProgress, showErrorMessage } from '../../../shared/utils/notifications';
import { ToolbarNavActions, ToolbarNavHeaderSelectOptions } from './models';

export const ToolbarnavOverflowMenuItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_toolbarnav_overflow_menu_item', // @see initialize()
    model: null, // ToolbarNavAction
    tagName: 'li',

    templateHelpers: function () {
        var i18n_key_text = this.model.get('i18n_key_text');
        return {
            text: _t(i18n_key_text) || i18n_key_text,
            icon: this.model.get('icon')
        };
    }
});

export const ToolbarnavOverflowMenu = Backbone.Marionette.CollectionView.extend({
    collection: null, // ToolbarNavActions
    itemView: ToolbarnavOverflowMenuItem, // ToolbarnavOverflowMenuItem
    tagName: 'ul',

    ANIMATION_TIME_MS: 150,

    onShow: function () {
        var target_height = this.$el.css('height');
        var $overflow_menu = this.$el.parent();

        $overflow_menu.velocity(
            {
                height: target_height
            },
            {
                duration: this.ANIMATION_TIME_MS,
                easing: 'easeOutQuint',
                begin: function () {
                    $overflow_menu.css({
                        height: 0
                    });
                }
            }
        );
    },

    close: function () {
        var this_ = this;
        var arguments_ = arguments;

        var $overflow_menu = this.$el.parent();

        $overflow_menu.velocity(
            {
                opacity: 0
            },
            {
                duration: this.ANIMATION_TIME_MS,
                easing: 'easeOutQuint',
                complete: function () {
                    $overflow_menu.attr('style', ''); // clear whatever custom styling we've done for next time

                    ToolbarnavOverflowMenu.__super__.close.apply(this_, arguments_);
                }
            }
        );
    }
});

export const ToolbarnavSelectOption = Backbone.Marionette.ItemView.extend({
    template: '#tpl_toolbarnav_select_option',
    model: null, // ToolbarNavHeaderSelectOption
    tagName: 'li'
});

export const ToolbarnavSelectOptions = ToolbarnavOverflowMenu.extend({
    collection: null, // ToolbarNavHeaderSelectOptions
    itemView: ToolbarnavSelectOption,

    ANIMATION_TIME_MS: 300 // often a longer list, so lets give it a little more time to unroll
});

/**
 * Our shared toolbar
 */
export const ToolbarNav = Backbone.Marionette.Layout.extend({
    template: '#tpl_toolbarnav',
    page_title: null,
    page_subtitle: null,
    show_back_button: false,
    show_add_task: false, // defined and passed in from BaseToolbarNavPageLayout
    show_edit_sensor: false, // defined and passed in from BaseToolbarNavPageLayout
    toggle_search: false, // defined and passed in from BaseToolbarNavPageLayout
    create_task_options: null, // {} or function - extra options to send to ProjectCreator() if the user taps it
    parent_page: null, // BaseToolbarNavPage(Layout) that encompasses this view
    center_title: false, // if true, title h1 will be centered

    actions: null, // ToolbarNavActions - actions to show in the overflow menu
    _overflow_actions: null, // ToolbarNavActions - a filtered set :of this.actions that will appear in the overflow menu
    _overflow_tasks_actions: null, // ToolbarNavActions - actions for fast task creation
    _primary_action: null, // ToolbarNavAction - single primary action to show outisde of the overflow menu
    // This shouldn't be set explicitly; we extract this from actions by looking for any actions with display:icon
    toolbarnav_select_options: null, // ToolbarnavSelectOptions - Search options to replace the page title with

    _sensor_model: null, // Set when this component is in a sensor context. Enables sensor settings.

    triggers: {
        go_back__noop: 'toolbarnav:go_back'
    },

    templateHelpers: function () {
        return {
            title: this.page_title,
            subtitle: this.page_subtitle,
            show_back_button: this.show_back_button,
            primary_action: this._primary_action,
            show_add_task: this.show_add_task,
            show_edit_sensor: this.show_edit_sensor,
            toggle_search: this.toggle_search,
            show_overflow_menu: this._overflow_actions && this._overflow_actions.length > 0,
            center_title: this.center_title,
            show_select_options: this.show_select_options(),
            notification_count: $f.mobile.unread_notifications_count.get('count')
        };
    },

    regions: {
        r_overflow_menu: '.overflow_menu',
        r_toolbar_select_options: '.select_options_container'
    },

    events: {
        'click #action_left.toggle_leftmenu': 'toggle_leftmenu',
        'click #action_left.go_back': 'click_go_back',
        'click .quick_add_task': 'show_tasks_menu',

        // we use HammerJS to listen for tap/pan on .overflow_touchblock.
        'click .show_overflow_menu': 'show_overflow_menu',
        'click .title_container': 'show_select_options_list',

        'click .edit_sensor': 'show_edit_sensor_page',
        'click .create_tasks': 'show_create_tasks',
        'click .create_project': 'show_create_project',
        'click .create_announcement': 'show_create_announcement',
        'click .overflow_menu a': function (event) {
            this.hide_overflow_menu(); // @author:david do not prevent default so other menu item events fire
        }
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        // Create/Backbone-ify actions:
        this.actions || (this.actions = []);
        if (!(this.actions instanceof Backbone.Collection)) {
            this.actions = new ToolbarNavActions(this.actions);
        }

        // Create/Backbone-ify toolbarnav_select_options:
        if (this.toolbarnav_select_options && this.toolbarnav_select_options.length > 0) {
            this.toolbarnav_select_options = new ToolbarNavHeaderSelectOptions(this.toolbarnav_select_options);

            this.page_title = this.toolbarnav_select_options.findWhere({ is_selected: true }).get('display_value');
        }

        // Re-render when our notification count changes
        this.listenTo($f.mobile.unread_notifications_count, 'change', this.render);
    },

    onBeforeRender: function () {
        var this_ = this;

        if (this.actions.where({ display: 'icon' }).length > 1) {
            console.warn('Cannot have multiple Toolbarnav Actions with display:icon. Defaulting to just one.');
        }

        this._overflow_actions = new ToolbarNavActions(this.actions.toJSON());
        this._overflow_tasks_actions = new ToolbarNavActions();
        this._primary_action = this._overflow_actions.findWhere({ display: 'icon' });
        this._overflow_actions.remove(this._primary_action);

        var actions = [
            {
                icon: 'df-tasks',
                container_class: 'create_tasks',
                i18n_key_text: 'mobile_tasks_create_task',
                display: 'overflow'
            },
            {
                icon: 'df-projects',
                container_class: 'create_project',
                i18n_key_text: 'mobile_projects_create_project',
                display: 'overflow'
            },
            {
                icon: 'df-announcement',
                container_class: 'create_announcement',
                i18n_key_text: 'mobile_announcements_create',
                display: 'overflow'
            }
        ];

        this._overflow_tasks_actions.add(actions);

        // only let show_add_task be true if we have permission
        if (!this.show_add_task) {
            return;
        }
        this_.show_add_task = true;
    },

    onRender: function () {
        if (this.page_subtitle) {
            this.$el.addClass('has_subtitle');
        } else {
            this.$el.removeClass('has_subtitle');
        }

        if (this.templateHelpers().show_overflow_menu) {
            this.$el.addClass('has_overflow_menu');
        } else {
            this.$el.removeClass('has_overflow_menu');
        }
    },

    // We pass onShow down from ToolbarNavPage so we can use it here
    on_show: function () {
        showUploadProgress();
    },

    _show_touchblock: function () {
        var this_ = this;

        $f.mobile.show_overflow_touchblock($('.overflow_touchblock'), function () {
            this_.hide_overflow_menu();
            this_.hide_select_options_list();
            $f.mobile.hide_overflow_touchblock();

            return false;
        });
    },

    show_overflow_menu: function (event) {
        if (!this._overflow_actions || !this._overflow_actions.length) {
            return false;
        }

        this.r_overflow_menu.show(new ToolbarnavOverflowMenu({ collection: this._overflow_actions }));
        this._show_touchblock();
    },

    hide_overflow_menu: function (event) {
        this.r_overflow_menu.close();
        $f.mobile.hide_overflow_touchblock();
        return false;
    },

    show_select_options_list: function () {
        if (
            $f.active_user.is_submitter() ||
            !this.toolbarnav_select_options ||
            this.toolbarnav_select_options.length == 0
        ) {
            return false;
        }

        this.r_toolbar_select_options.show(
            new ToolbarnavSelectOptions({
                collection: this.toolbarnav_select_options
            })
        );

        this._show_touchblock();

        return false;
    },

    set_sensor_model: function (sensor) {
        this._sensor_model = sensor;
    },

    show_edit_sensor_page: function (event) {
        event.preventDefault();

        import('../../../backbone-unorganized/sensors/views').then((module) => {
            $f.mobile.nav.show(module.SensorSettingsPage, {
                model: this._sensor_model
            });
        });
    },

    show_create_tasks: function (event) {
        event.preventDefault();

        import('../../../backbone-unorganized/tasks/views').then((module) => {
            $f.mobile.nav.show(module.FastTasksPage, {});
        });
    },

    show_create_announcement: function (event) {
        event.preventDefault();

        import('../../../backbone-unorganized/announcements/views').then((module) => {
            $f.mobile.nav.show(module.AnnouncementCreator, {});
        });
    },

    show_create_project: function (event) {
        event.preventDefault();

        var options = this.create_task_options || {};
        if (_.isFunction(options)) {
            options = options.apply(this.parent_page, []); // be sure to call this from the context of our parent page
        }

        import('../../../backbone-unorganized/views').then((module) => {
            $f.mobile.nav.show(module.ProjectCreator, options);
        });
    },

    /**
     * Default logic for whether or not to show the options dropdown carrot next to the main title
     */

    show_select_options: function () {
        return (
            !$f.active_user.is_submitter() && this.toolbarnav_select_options && this.toolbarnav_select_options.length
        );
    },

    hide_select_options_list: function (event) {
        this.r_toolbar_select_options.close();
        return false;
    },

    toggle_leftmenu: function () {
        $f.application.vent.trigger('mobile:toggle_leftmenu');
        return false;
    },

    // let our ToolbarnavPage know the user wants to go back. Each toolbarnav can behave differently.
    click_go_back: function () {
        this.trigger('toolbarnav:go_back');
        return false;
    },

    add_actions: function (actions) {
        var _actions = actions instanceof Backbone.Collection ? actions.toJSON() : actions;
        this.actions.add(_actions);
        this.render();
    },

    show_tasks_menu: function (event) {
        if (!isConnected()) {
            showErrorMessage(_t('mobile_message_error_create_task'));
            return false;
        }

        if ($f.active_user.is_submitter()) {
            this.show_create_tasks(event);
            return false;
        }

        this.r_overflow_menu.show(
            new ToolbarnavOverflowMenu({
                collection: this._overflow_tasks_actions
            })
        );

        this._show_touchblock();

        return false;
    }
});
